/**
 * Connection Timing Module
 *
 * Lightweight singleton that captures speed milestones per connection.
 * One active timing session at a time (only one connection happens at a time).
 *
 * T0 = when the spinner first appeared in index.html (window.__SPINNER_SHOWN_AT__).
 * This is the true start of the user's experience — everything is relative to it.
 * Falls back to Date.now() if no spinner timestamp (e.g., non-prefetch paths).
 *
 * Performance contract:
 * - mark() is synchronous: one Date.now() + one property set. Zero connection impact.
 * - flush() is async, fire-and-forget (no await in caller), called AFTER connection confirmed.
 * - Follows eventLogger.js pattern: Supabase insert with sendBeacon fallback.
 */

import { supabase, getAccessToken } from './supabase';
import { isProximityChannelActive } from './proximity/proximityService';

// ─── Module State ──────────────────────────────────────────────────────────────

let t0Perf = null; // performance.now() at T0 (for elapsed calculations)
let chatRoomId = null;
let userType = null;
let userLabel = null;
let milestones = {};

// ─── API ───────────────────────────────────────────────────────────────────────

/**
 * Start a new timing session. T0 = navigation start (performance.now() origin).
 * Anon T0 = navigation start (QR tap). Browser nav milestones captured automatically.
 * Profile T0 = when startTiming is called (QR screen open). No nav milestones.
 * @param {string} roomId - chatRoomId for this connection
 * @param {string} type - 'profile' or 'anon'
 * @param {string} [label] - User-identifying label (e.g., profile name)
 * @param {Object} [opts]
 * @param {number} [opts.t0Perf] - a profile session's T0 when it began earlier
 *   (performance.now() at that moment) — nearby connects start when the QR page
 *   began looking, not when the exchange lands (services/proximity/nearbyTiming)
 */
export function startTiming(roomId, type, label, { t0Perf: t0 } = {}) {
  chatRoomId = roomId;
  userType = type;
  userLabel = label || null;
  milestones = {};

  if (type === 'anon') {
    // Anon T0 = 0 (navigation start = when browser began loading after QR tap)
    t0Perf = 0;

    // Capture browser navigation milestones (the blank screen breakdown)
    try {
      const nav = performance.getEntriesByType('navigation')[0];
      if (nav) {
        milestones.ttfb = Math.round(nav.responseStart);
        milestones.html_downloaded = Math.round(nav.responseEnd);
        milestones.dom_ready = Math.round(nav.domInteractive);
      }
    } catch {
      // Navigation Timing API not available — skip
    }

    // Spinner shown timestamp from index.html inline script
    if (window.__SPINNER_SHOWN_AT__) {
      milestones.spinner_shown = Math.round(window.__SPINNER_SHOWN_AT__);
    }

    // Prefetch duration (how long the Supabase queries took, client-measured)
    if (window.__EXCHANGE_DATA__?.prefetchMs) {
      milestones.prefetch = {
        ms: Math.round((window.__SPINNER_SHOWN_AT__ || 0) + window.__EXCHANGE_DATA__.prefetchMs),
        duration: window.__EXCHANGE_DATA__.prefetchMs,
      };
    }
  } else {
    // Profile T0 = now (when QR screen opens / detection starts), or the given T0
    t0Perf = typeof t0 === 'number' ? t0 : performance.now();
  }

  const dataSource = window.__EXCHANGE_DATA__?.dataSource || null;
  console.log(
    `[CONNECTION_TIMING] Started: ${type} / ${roomId.slice(-6)} (source: ${dataSource})` +
      ` T0=${type === 'anon' ? 'navigation start' : 'now'}, perf=${Math.round(performance.now())}ms`
  );
}

/**
 * Record a milestone with elapsed ms from T0.
 * Synchronous — zero overhead on the connection path.
 * @param {string} name - Milestone name (e.g., 'ws_join_start')
 * @param {object} [metadata] - Optional extra data to store with this milestone
 */
export function mark(name, metadata) {
  if (t0Perf === null) return; // No active session
  const elapsed = Math.round(performance.now() - t0Perf);
  milestones[name] = metadata ? { ms: elapsed, ...metadata } : elapsed;
  console.log(`[CONNECTION_TIMING] ${name}: ${elapsed}ms`);
}

/**
 * Record a milestone measured elsewhere, as ms from this session's T0.
 * @param {string} name
 * @param {number} ms
 * @param {object} [metadata] - Optional extra data to store with this milestone
 */
export function markAt(name, ms, metadata) {
  if (t0Perf === null || typeof ms !== 'number') return;
  milestones[name] = metadata ? { ...metadata, ms: Math.round(ms) } : Math.round(ms);
}

/**
 * Fire-and-forget write to Supabase, then clear state.
 * Call AFTER connection is confirmed — never on the critical path.
 */
export function flush() {
  if (t0Perf === null || !chatRoomId) {
    console.log('[CONNECTION_TIMING] flush() called with no active session');
    return;
  }

  const totalMs = Math.round(performance.now() - t0Perf);

  // Capture values before clearing
  const row = {
    chat_room_id: chatRoomId,
    user_type: userType,
    milestones,
    user_label: userLabel,
    total_ms: totalMs,
    anon_detection_method: milestones.anon_detected?.method || null,
    proximity_active: isProximityChannelActive(),
    branch: process.env.REACT_APP_BRANCH || 'local',
  };

  console.log(
    `[CONNECTION_TIMING] Flushing: ${totalMs}ms total (start → chat ready), ` +
      `${Object.keys(milestones).length} milestones`
  );

  // Clear state immediately
  t0Perf = null;
  chatRoomId = null;
  userType = null;
  userLabel = null;
  milestones = {};

  // Fire-and-forget — errors swallowed
  supabase
    .from('connection_timings')
    .insert(row)
    .then(({ error }) => {
      if (error) {
        console.warn('[CONNECTION_TIMING] Insert failed:', error.message);
        flushViaBeacon(row);
      } else {
        console.log('[CONNECTION_TIMING] Saved to Supabase');
      }
    })
    .catch(() => {
      flushViaBeacon(row);
    });
}

/**
 * Unload-safe fallback: fetch+keepalive under the user's session. (Was
 * sendBeacon, which can't carry the session — timings need a signed-in
 * sender since the open-access lockdown.)
 */
function flushViaBeacon(row) {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
  const token = getAccessToken();
  if (!supabaseUrl || !supabaseKey || !token) return;

  try {
    fetch(`${supabaseUrl}/rest/v1/connection_timings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: supabaseKey,
        Authorization: `Bearer ${token}`,
        Prefer: 'return=minimal',
      },
      body: JSON.stringify(row),
      keepalive: true,
    }).catch(() => console.warn('[CONNECTION_TIMING] keepalive fallback failed'));
    console.log('[CONNECTION_TIMING] Sent via keepalive fallback');
  } catch {
    console.warn('[CONNECTION_TIMING] keepalive fallback failed');
  }
}
